/* =========================================================================
   iUROP — Buscador overlay (predictive search) · KLEOS
   - Se abre/cierra desde cualquier [data-search-toggle] del header; mientras
     está abierto se pone aria-expanded="true" en TODOS los toggles y el CSS
     (snippets/iurop-search.liquid) intercambia el icono lupa → X.
   - Si el header tiene un input propio ([data-search-input] o, como respaldo,
     el input de un form[role="search"] fuera del overlay), el panel se
     despliega debajo del header y se sincroniza con él (diseño desktop).
   - Predictive search real: /search/suggest + Section Rendering API
     (sección iurop-predictive-search) con la tarjeta estándar
     (snippets/iurop-product-card; el quick-add lo gestiona
     assets/iurop-product-card.js y aquí solo cerramos al oír 'cart:added').
   - MÉTODOS DE CIERRE: X del panel (desktop, arriba-derecha) · X de la barra
     (móvil) · icono del header (lupa→X) · click en el backdrop · tecla Esc ·
     click en cualquier enlace/resultado · quick-add (cierra y deja paso al
     side-cart). Todas delegadas en [data-search-close] o cierre por navegación.
   - Estado: discrimina por la clase is-open (convención §5.bis, sin hidden).
   - Accesible: role=dialog, focus-trap, foco devuelto al opener al cerrar.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__iuropSearchInit) return;
  window.__iuropSearchInit = true;

  var root = document.getElementById('iurop-search');
  if (!root) return;

  var panel = root.querySelector('[data-search-panel]');
  var overlayInput = root.querySelector('[data-search-overlay-input]');
  var resultsEl = root.querySelector('[data-search-results]');
  var defaultEl = root.querySelector('[data-search-default]');
  var sectionId = root.getAttribute('data-predictive-section') || 'iurop-predictive-search';
  var routeRoot = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  var MIN_CHARS = 2;

  var externalInputs = [];
  var currentOpener = null;
  var lastQuery = '';
  var controller = null;
  var debounceTimer = null;
  // Al cerrar devolvemos el foco al opener (a11y). Si el opener es el input del
  // header, su handler 'focus' REABRIRÍA el overlay → este flag lo suprime
  // durante esa re-focalización (focus() dispara el evento de forma síncrona).
  var suppressReopen = false;

  function isOpen() {
    return root.classList.contains('is-open');
  }

  function isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  /* ---- posición del panel bajo el header ---- */
  function positionPanel() {
    var top = 0;
    var anchor = null;
    if (currentOpener && currentOpener.closest) {
      anchor = currentOpener.closest('.shopify-section, header, [data-header]');
    }
    if (!anchor) {
      anchor = document.querySelector('[data-search-anchor], .shopify-section-group-header-group, header, [data-header]');
    }
    if (anchor) {
      var r = anchor.getBoundingClientRect();
      top = Math.max(0, Math.round(r.bottom));
    }
    if (!top) top = 70;
    root.style.setProperty('--iurop-search-top', top + 'px');
  }

  /* ---- inputs externos (buscador del header en desktop) ---- */
  function collectExternalInputs() {
    var found = Array.prototype.slice
      .call(document.querySelectorAll('[data-search-input], form[role="search"] input[name="q"]'))
      .filter(function (el) {
        return !el.hasAttribute('data-search-overlay-input') && !root.contains(el);
      });
    // sin duplicados
    externalInputs = found.filter(function (el, i) { return found.indexOf(el) === i; });
    externalInputs.forEach(bindExternalInput);
    var anyVisible = externalInputs.some(isVisible);
    root.classList.toggle('iurop-search--standalone', !anyVisible);
  }

  function bindExternalInput(el) {
    if (el.dataset.iuropSearchBound) return;
    el.dataset.iuropSearchBound = '1';
    el.setAttribute('autocomplete', 'off');
    el.addEventListener('input', function () {
      if (!isOpen()) { currentOpener = el; open(); }
      debouncedSearch(el.value, el);
    });
    el.addEventListener('focus', function () {
      if (suppressReopen) return;
      if (!isOpen()) { currentOpener = el; open(); }
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitSearch(el.value); }
    });
  }

  function syncInputs(value, except) {
    var list = externalInputs.concat(overlayInput ? [overlayInput] : []);
    list.forEach(function (el) {
      if (el !== except && el.value !== value) el.value = value;
    });
  }

  /* ---- focus-trap ---- */
  function focusables() {
    var inside = Array.prototype.slice.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')
    ).filter(isVisible);
    var externals = externalInputs.filter(isVisible);
    return externals.concat(inside);
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || items.indexOf(active) === -1)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || items.indexOf(active) === -1)) {
      e.preventDefault();
      first.focus();
    }
  }

  function setTogglesExpanded(expanded) {
    document.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  /* ---- abrir / cerrar (estado = clase is-open) ---- */
  function open() {
    if (isOpen()) return;
    collectExternalInputs();
    positionPanel();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    setTogglesExpanded(true);
    document.documentElement.classList.add('iurop-search-locked');
    document.addEventListener('keydown', onKeydown, true);
    // El top del panel = borde inferior EXACTO del header. Recalcularlo en
    // resize Y en scroll mantiene el panel pegado al header aunque sea sticky
    // (getBoundingClientRect es relativo al viewport). Scroll pasivo y barato.
    window.addEventListener('resize', positionPanel);
    window.addEventListener('scroll', positionPanel, { passive: true });

    var externalVisible = externalInputs.filter(isVisible);
    var target = (!isMobile() && externalVisible.length) ? externalVisible[0] : overlayInput;
    if (target) {
      setTimeout(function () {
        target.focus();
        if (target.setSelectionRange && target.value) {
          try { target.setSelectionRange(target.value.length, target.value.length); } catch (err) { /* noop */ }
        }
      }, 60);
    }
    // si ya había texto (p. ej. del input del header) lanza la búsqueda
    if (target && target.value.trim().length >= MIN_CHARS) runSearch(target.value);
  }

  function close() {
    if (!isOpen()) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    setTogglesExpanded(false);
    document.documentElement.classList.remove('iurop-search-locked');
    document.removeEventListener('keydown', onKeydown, true);
    window.removeEventListener('resize', positionPanel);
    window.removeEventListener('scroll', positionPanel, { passive: true });

    // devolver el foco a quien abrió el overlay SIN reabrir (flag síncrono)
    if (currentOpener && document.contains(currentOpener) && isVisible(currentOpener)) {
      suppressReopen = true;
      try { currentOpener.focus(); } catch (err) { /* noop */ }
      suppressReopen = false;
    }
  }

  function toggle(opener) {
    currentOpener = opener || currentOpener;
    if (isOpen()) close(); else open();
  }

  /* ---- vistas ---- */
  function showDefault() {
    if (resultsEl) { resultsEl.hidden = true; resultsEl.innerHTML = ''; }
    if (defaultEl) defaultEl.hidden = false;
    root.classList.remove('is-searching');
  }

  function showResults() {
    if (defaultEl) defaultEl.hidden = true;
    if (resultsEl) resultsEl.hidden = false;
    root.classList.add('is-searching');
  }

  /* ---- predictive fetch (/search/suggest + Section Rendering API) ---- */
  function runSearch(value, sourceEl) {
    var q = (value || '').trim();
    syncInputs(q, sourceEl);
    if (q.length < MIN_CHARS) { lastQuery = ''; showDefault(); return; }
    if (q === lastQuery && resultsEl && resultsEl.innerHTML) { showResults(); return; }
    lastQuery = q;
    showResults();
    if (resultsEl) resultsEl.classList.add('is-loading');

    if (controller) controller.abort();
    controller = new AbortController();

    var url = routeRoot +
      '/search/suggest?q=' + encodeURIComponent(q) +
      '&section_id=' + encodeURIComponent(sectionId) +
      '&resources[type]=product,query,collection' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,variants.title,vendor';

    fetch(url, { signal: controller.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('suggest ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (!resultsEl) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-predictive-content]');
        resultsEl.innerHTML = content ? content.outerHTML : '';
        resultsEl.classList.remove('is-loading');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (resultsEl) resultsEl.classList.remove('is-loading');
      });
  }

  function debouncedSearch(value, sourceEl) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { runSearch(value, sourceEl); }, 250);
  }

  function submitSearch(value) {
    var q = (value || '').trim();
    if (!q) return;
    window.location.href = routeRoot +
      '/search?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
  }

  /* ---- eventos ---- */
  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }
    trapFocus(e);
  }

  // abrir/cerrar desde el icono del header (lupa ↔ X)
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-search-toggle]');
    if (t) { e.preventDefault(); toggle(t); }
  });

  // clic dentro del overlay: X / backdrop cierran; cualquier enlace cierra y navega
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-search-close]')) { e.preventDefault(); close(); return; }
    if (e.target.closest('[data-iurop-quick-add]')) return; // lo gestiona iurop-product-card.js
    if (e.target.closest('a[href]')) close(); // cerrar y dejar navegar
  });

  // quick-add de las tarjetas (assets/iurop-product-card.js): al añadirse el
  // producto se abre el side-cart -> cerramos el buscador para dejarle sitio.
  document.addEventListener('cart:added', function () {
    if (isOpen()) close();
  });

  // escribir / Enter en el input del propio overlay
  if (overlayInput) {
    overlayInput.addEventListener('input', function () {
      debouncedSearch(overlayInput.value, overlayInput);
    });
  }
  var form = root.querySelector('[data-search-form]');
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitSearch(overlayInput ? overlayInput.value : '');
    });
  }

  // inputs del header presentes al cargar (los añadidos después se captan al abrir)
  collectExternalInputs();
})();
